import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MetricCard = ({ icon: Icon, title, value, color, cases = [], onCaseClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Generate mock trend data for last 7 days
  const generateTrendData = () => {
    const labels = [];
    const data = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      labels.push(format(date, 'MMM dd'));
      
      // Simulate trend based on current value
      const variation = Math.random() * 0.3 - 0.15; // ±15% variation
      const baseValue = value * (1 - (i * 0.05)); // Gradual increase
      data.push(Math.max(0, Math.floor(baseValue + baseValue * variation)));
    }
    
    return { labels, data };
  };

  const trendData = generateTrendData();
  
  const chartData = {
    labels: trendData.labels,
    datasets: [
      {
        label: title,
        data: trendData.data,
        fill: true,
        backgroundColor: `${color}20`,
        borderColor: color,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  // Calculate trend
  const trend = trendData.data.length > 1 
    ? ((trendData.data[trendData.data.length - 1] - trendData.data[trendData.data.length - 2]) / trendData.data[trendData.data.length - 2] * 100)
    : 0;

  return (
    <div className="dashboard-card">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
      >
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
            <Icon style={{ color }} size={24} />
          </div>
          <div className="flex items-center gap-2">
            {trend !== 0 && (
              <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                <span>{Math.abs(trend).toFixed(1)}%</span>
              </div>
            )}
            {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </div>
        </div>
        <div className="text-3xl font-bold text-slate-900" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
          {value}
        </div>
        <div className="text-sm text-slate-600 mt-1">{title}</div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-6 pt-6 border-t border-slate-200 animate-in slide-in-from-top-2">
          {/* Chart */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">7-Day Trend</h4>
            <div style={{ height: '180px' }}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Cases List */}
          {cases && cases.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-slate-900">Recent Cases</h4>
                <span className="text-xs text-slate-500">{cases.length} total</span>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cases.slice(0, 5).map((caseItem, idx) => (
                  <div
                    key={caseItem.id || idx}
                    onClick={() => onCaseClick && onCaseClick(caseItem)}
                    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">
                          {caseItem.company_name || caseItem.client_name || 'Unnamed Case'}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {caseItem.processor_name || 'No processor'}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-sm font-semibold text-slate-900">
                          ${(caseItem.frozen_amount || 0).toLocaleString()}
                        </p>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full mt-1 ${
                          caseItem.status === 'resolved' ? 'bg-green-100 text-green-700' :
                          caseItem.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          caseItem.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {caseItem.status || 'new'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {cases.length > 5 && (
                  <button className="w-full text-center text-sm text-purple-600 hover:text-purple-700 font-medium py-2">
                    View all {cases.length} cases →
                  </button>
                )}
              </div>
            </div>
          )}

          {(!cases || cases.length === 0) && (
            <div className="text-center py-6 text-slate-500 text-sm">
              No cases in this category
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetricCard;
